import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import WorkspaceService from "services/workspace-service";

import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";

/**
 * ⚡ SocialReportingModal – clean version with only the Reports table for downgrade limits
 */
function SocialReportingModal() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // ─────────────────────────────────────────────
    // Fetch Reports List
    // ─────────────────────────────────────────────
    const fetchReports = async () => {
        try {
            setLoading(true);
            const data = await WorkspaceService.fetchWorkspaceReports();

            const enriched = (data || []).map((report) => {
                return {
                    ...report,
                    selectedProfiles: report.config?.selectedProfiles || [],
                };
            });

            setReports(enriched);
        } catch (err) {
            console.error("❌ Error loading reports:", err);
            setError("Failed to load reports.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    // ─────────────────────────────────────────────
    // Delete
    // ─────────────────────────────────────────────
    const handleDeleteClick = (report) => {
        setReportToDelete(report);
        setIsConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!reportToDelete) return;

        try {
            setDeleting(true);
            // Hard delete: Deletes the report
            await WorkspaceService.deleteWorkspaceReport(reportToDelete._id);
            setIsConfirmDialogOpen(false);
            fetchReports();
        } catch (err) {
            console.error("❌ Failed to delete report:", err);
        } finally {
            setReportToDelete(null);
            setDeleting(false);
        }
    };

    // ─────────────────────────────────────────────
    // Avatar Renderer
    // ─────────────────────────────────────────────
    const renderAvatars = (profiles) => {
        if (!Array.isArray(profiles)) return null;
        return (
            <MDBox display="flex" justifyContent="left" alignItems="left">
                <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 28, height: 28 } }}>
                    {profiles.map((profile, idx) => (
                        <Tooltip key={idx} title={profile.name || "Profile"} arrow>
                            <Avatar
                                src={profile.profilePictureUrl || ""}
                                alt={profile.name || "P"}
                                sx={{ width: 28, height: 28 }}
                            />
                        </Tooltip>
                    ))}
                </AvatarGroup>
            </MDBox>
        );
    };

    // ─────────────────────────────────────────────
    // Table Columns
    // ─────────────────────────────────────────────
    const columns = [
        {
            Header: (
                <MDBox py={1} px={2} pl={3} sx={{ minWidth: "250px" }}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        NAME
                    </MDTypography>
                </MDBox>
            ),
            accessor: "reportName",
            align: "left",
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        PERIOD
                    </MDTypography>
                </MDBox>
            ),
            accessor: "period",
            align: "left",
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        PROFILES
                    </MDTypography>
                </MDBox>
            ),
            accessor: "profiles",
            align: "left",
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        RECIPIENT(S)
                    </MDTypography>
                </MDBox>
            ),
            accessor: "recipient",
            align: "left",
        },
        {
            Header: (
                <MDBox py={1} px={2}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        ACTION
                    </MDTypography>
                </MDBox>
            ),
            accessor: "action",
            align: "center",
        },
    ];

    const rows = reports.map((report) => {
        const isInactive = report.status?.toLowerCase() === "inactive";
        const rowOpacity = isInactive ? 0.5 : 1;

        // Wrap row content function
        const wrapCell = (content) => (
            <MDBox sx={{ opacity: rowOpacity }}>
                {content}
            </MDBox>
        );

        return {
            reportName: wrapCell(report.report_name || "—"),
            period: wrapCell(report.config?.period?.replace(/_/g, ' ')?.toUpperCase() || "—"),
            profiles: wrapCell(
                report.selectedProfiles && report.selectedProfiles.length > 0
                    ? renderAvatars(report.selectedProfiles)
                    : "—"
            ),
            recipient: wrapCell(
                Array.isArray(report.recipient_emails)
                    ? report.recipient_emails.join(", ")
                    : report.recipient_emails || "—"
            ),
            action: isInactive ? (
                <Tooltip title="Deleted Report" arrow placement="bottom">
                    <MDTypography variant="caption" color="text">Deleted</MDTypography>
                </Tooltip>
            ) : (
                <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(report)}
                >
                    <DeleteIcon color="error" fontSize="small" />
                </IconButton>
            ),
        };
    });

    return (
        <MDBox p={2} sx={{ width: "100%" }}>
            <Card sx={{ p: 2 }}>
                <MDBox
                    mx={2}
                    mt={-5}
                    mb={2}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    flexDirection="row"
                >
                    <MDTypography variant="h6" color="white">
                        Report Library
                    </MDTypography>
                </MDBox>

                {/* Loader */}
                {loading ? (
                    <MDBox display="flex" justifyContent="center" py={5}>
                        <CircularProgress color="warning" />
                    </MDBox>
                ) : error ? (
                    <MDTypography color="error">{error}</MDTypography>
                ) : (
                    <DataTable
                        canSearch={false}
                        isSorted={false}
                        showTotalEntries={false}
                        noEndBorder
                        entriesPerPage={false}
                        table={{ columns, rows }}
                    />
                )}
            </Card>

            {/* Delete Confirmation */}
            <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}
                sx={{ zIndex: 2000 }}
            >
                <DialogTitle>Delete Report</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete report{" "}
                        <strong>{reportToDelete?.report_name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}

export default SocialReportingModal;
